import React, { useCallback, useEffect, useState } from 'react';
import { medicalProfileFilesService } from '../../../../../primary-record-types/apiTypes';
import {
    UseMedicalProfileFileImageResponse,
    useMedicalProfileBufferedFileImage,
} from '../../../../../hooks/api/useMedicalProfileFileImage';
import Alert from '../../../../v2BaseComponents/Alert';
import { getIntl } from '../../../../../utils/intl';
import SizedView, { Dimensions } from '../../../../v2BaseComponents/SizedView';
import PageDisplay, { PageDisplayProps } from '../../../../shared/FileDisplay/PageDisplay';
import { useIsMobileLayout } from '../../../../../hooks/useIsMobileLayout';
import { MedicalProfileFiles } from '../../../../../primary-record-types';
import BoundingBoxDisplay from '../../../../shared/FileDisplay/BoundingBoxDisplay';
import MountAfterDelay from '../../../../shared/MountAfterDelay';
import Skeleton from '../../../../v2BaseComponents/Skeleton';
import IconButton from '../../../../v2BaseComponents/IconButton';
import Stack, { ContentStack } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';

const intl = getIntl('FileViewer', {
    errorLoadingPage: 'Error loading page',
    page: 'Page {activePage}/{pageCount}',
});

export type FileViewerOwnProps = {
    file: medicalProfileFilesService.MedicalProfileFileListItem;
    pageCount: number;
    defaultPage?: number;
    highlightBoundingBox?: MedicalProfileFiles.BoundingBox;
};

export type FileViewerProps = FileViewerOwnProps;

const getPageDisplay = (
    page: UseMedicalProfileFileImageResponse,
    containerWidth: number,
    pageNumber: number,
    isVisible: boolean,
    boundingBox: MedicalProfileFiles.BoundingBox | null,
) => {
    if (page.status !== 'loaded') return null;

    const props: PageDisplayProps = {
        fileUrl: page.image.downloadUrl,
        containerDimensions: { width: containerWidth },
        pageDimensions: page.image.dimensions,
        type: page.image.type,
        rotation: page.image.rotation,
    };
    return (
        <BoundingBoxDisplay
            boundingBox={boundingBox}
            key={pageNumber}
            rotation={page.image.rotation}
            imageType={page.image.type}
        >
            <PageDisplay {...props} noSkeleton identifier={String(pageNumber)} isVisible={isVisible} enableFocus />
        </BoundingBoxDisplay>
    );
};

const FileViewer: React.FC<FileViewerProps> = ({ file, pageCount, defaultPage, highlightBoundingBox }) => {
    const isMobileLayout = useIsMobileLayout();
    const [activePage, setActivePage] = useState(defaultPage || 1);
    const [boundingBox, setBoundingBox] = useState<MedicalProfileFiles.BoundingBox | undefined>(highlightBoundingBox);
    // reset bounding box when page changes
    useEffect(() => {
        if (activePage !== defaultPage) {
            setBoundingBox(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePage]);
    const { activePageImage, bufferedPages } = useMedicalProfileBufferedFileImage({
        medicalProfileId: file.medicalProfileId,
        medicalProfileFileId: file.medicalProfileFileId,
        page: activePage,
        pageCount: pageCount,
    });

    const renderMainContent = useCallback(
        (page: UseMedicalProfileFileImageResponse, containerWidth: number, pageNumber: number) => {
            const loadingFallback = (
                <Skeleton
                    key={pageNumber}
                    style={{
                        width: containerWidth,
                        height: containerWidth * (11 / 8.5),
                    }}
                />
            );
            if (page.status === 'loading') {
                return loadingFallback;
            } else if (page.status === 'loaded') {
                return getPageDisplay(page, containerWidth, pageNumber, true, boundingBox || null);
            } else {
                // during state transitions, we may have a page that is not loaded and don't want to flash an abrupt error box
                return (
                    <MountAfterDelay key={pageNumber} delay={3000} fallback={loadingFallback}>
                        <Alert key={pageNumber} type="error" title={intl('errorLoadingPage')} />
                    </MountAfterDelay>
                );
            }
        },
        [boundingBox],
    );

    const renderPageSwitcher = (isBack: boolean) => {
        const isBackDisabled = activePage <= 1;
        const isForwardDisabled = activePage >= pageCount;
        const isDisabled = isBack ? isBackDisabled : isForwardDisabled;
        return (
            <IconButton
                isDisabled={isDisabled}
                icon={isBack ? 'ChevronLeft' : 'ChevronRight'}
                onPress={() => {
                    if (!isDisabled) {
                        setActivePage(isBack ? activePage - 1 : activePage + 1);
                    }
                }}
                variant="transparent"
            />
        );
    };

    const renderSizedView = useCallback(
        ({ width }: Dimensions) => {
            return [activePageImage, ...bufferedPages]
                .sort((a, b) => a.page - b.page)
                .map((maybeBufferedPage) =>
                    maybeBufferedPage.page === activePage
                        ? renderMainContent(maybeBufferedPage.image, width, maybeBufferedPage.page)
                        : getPageDisplay(maybeBufferedPage.image, width, maybeBufferedPage.page, false, null),
                );
        },
        [activePage, activePageImage, bufferedPages, renderMainContent],
    );

    return (
        <ContentStack>
            <Stack horizontal justify="center" fullWidth align="center">
                {!isMobileLayout ? renderPageSwitcher(true) : null}
                <SizedView style={{ flex: 1, maxWidth: 600 }}>{renderSizedView}</SizedView>
                {!isMobileLayout ? renderPageSwitcher(false) : null}
            </Stack>
            <Stack horizontal justify={'center'} space={4} align={'center'} fullWidth>
                {isMobileLayout ? renderPageSwitcher(true) : null}
                <Text variant="subtitle-2">{intl('page', { activePage, pageCount })}</Text>
                {isMobileLayout ? renderPageSwitcher(false) : null}
            </Stack>
        </ContentStack>
    );
};

export default React.memo(FileViewer);
